import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@/contexts/TranslationContext';

/**
 * Hook to translate a single text string
 */
export function useTranslatedText(originalText: string, cacheKey?: string) {
  const { translate, currentLanguage, isTranslating } = useTranslation();
  const [translatedText, setTranslatedText] = useState(originalText);

  useEffect(() => {
    let cancelled = false;
    
    const doTranslate = async () => {
      const result = await translate(originalText, cacheKey);
      if (!cancelled) {
        setTranslatedText(result);
      }
    };

    if (currentLanguage === 'en') {
      setTranslatedText(originalText);
    } else {
      doTranslate();
    }

    return () => {
      cancelled = true;
    };
  }, [originalText, cacheKey, translate, currentLanguage]);

  return { text: translatedText, isTranslating };
}

/**
 * Hook to translate multiple text strings at once
 */
export function useTranslatedTexts(originalTexts: string[]) {
  const { translateBatch, currentLanguage, isTranslating } = useTranslation();
  const [translatedTexts, setTranslatedTexts] = useState<string[]>(originalTexts);

  useEffect(() => {
    let cancelled = false;

    const doTranslate = async () => {
      const results = await translateBatch(originalTexts);
      if (!cancelled) {
        setTranslatedTexts(results);
      }
    };

    if (currentLanguage === 'en') {
      setTranslatedTexts(originalTexts);
    } else {
      doTranslate();
    }

    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(originalTexts), translateBatch, currentLanguage]);

  return { texts: translatedTexts, isTranslating };
}

/**
 * Hook to get a translation function for on-demand translation
 */
export function useTranslator() {
  const { translate, translateBatch, currentLanguage, isTranslating } = useTranslation();
  
  const t = useCallback(async (text: string) => {
    if (currentLanguage === 'en') return text;
    return translate(text);
  }, [translate, currentLanguage]);

  return { t, translateBatch, isTranslating, currentLanguage };
}
